import React, { useState, useMemo, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { socket } from "../utils/socket";
import { useDashboard } from "./dashboardStore";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const UserDocumentStorage = () => {
	const [items, setItems] = useState([]);
	const { instance } = useMsal();
	const [userDisplayName, setUserDisplayName] = useState(
		instance.getActiveAccount()?.name || "UNKNOWN USER"
	);

	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [showModal, setShowModal] = useState(false);

	const [newDoc, setNewDoc] = useState({
		file: null,
		fileType: "General",
		tags: "",
		description: "",
	});

	const [errors, setErrors] = useState({});
	const pageSize = 10;
	const [loading, setLoading] = useState(false);


  // Subscribe to dashboard store slice
const documentsDat = useDashboard(state => state.data?.documents);

// -------------------------------------
// Process documentsDat requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!documentsDat) return;

  try {
    // Save to state
        setItems(documentsDat);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [documentsDat]); // Reactive: runs whenever store updates


	// Socket listener for document updates (can expand later)
	useEffect(() => {
		if (!socket) return;

		const handleDocAlert = (data) => {
			if (data.type === "singleNewDocument") {
            
     			const addedDoc = data.newDocument;
     			setItems(items => [addedDoc, ...items]);


			}
			if (data.type === "documentDeleted") {
                setItems(items => items.filter((i) => i.DocumentID !== Number(data.id)));
			}
		};

		socket.on("DocumentStorageAlert", handleDocAlert);
		return () => {
			socket.off("DocumentStorageAlert", handleDocAlert);
		};
	}, [socket]);

	// Validate document before upload
	const validateDoc = () => {
		const newErrors = {};
		const MAX_SIZE = 10 * 1024 * 1024; // 5 MB

		if (!newDoc.file) newErrors.file = true;
		if (newDoc.file?.size > MAX_SIZE) newErrors.file = "File too large! Max 10 MB allowed.";
		if (!newDoc.description || newDoc.description.trim() === "") newErrors.description = true;
		if (!newDoc.tags || newDoc.tags.trim() === "") newErrors.tags = true;

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Upload document to backend
	const handleAdd = async () => {
		if (!validateDoc()) return;

		const file = newDoc.file;
		if (!file) return alert("No file selected");

		try {
			const formData = new FormData();
			formData.append("file", file);

			const uploadRes = await fetch(`${apiBaseUrl}/api/documents/upload`, {
				method: "POST",
				body: formData,
			});
			if (!uploadRes.ok) throw new Error("Blob upload failed");

			const uploadData = await uploadRes.json();

			// Save metadata
			const metadataRes = await fetch(`${apiBaseUrl}/api/documents/insert`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					fileName: file.name,
					blobUrl: uploadData.blobUrl,
					uploadedBy: userDisplayName,
					fileType: newDoc.fileType || "General",
					description: newDoc.description,
					tags: newDoc.tags,
					fileSize: file.size,
				}),
			});
			if (!metadataRes.ok) throw new Error("Failed to save document metadata");


			setPage(1);
			setShowModal(false);
			setNewDoc({ file: null, fileType: "General", tags: "", description: "" });
			setErrors({});
		} catch (err) {
			console.error("Error adding document:", err);
			alert("Failed to upload document. Try again.");
		}
	};

	// Delete document
	const handleDelete = async (id) => {
		if (!window.confirm("⚠️ Are you sure you want to delete this cost?")) return;

		try {
			const res = await fetch(`${apiBaseUrl}/api/documents/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete document");

		} catch (err) {
			console.error("Error deleting document:", err);
			alert("Failed to delete. Try again.");
		}
	};

	// Filter and sort items
	const filteredItems = useMemo(() => {
		return items
			.sort((a, b) => b.DocumentID - a.DocumentID)
			.filter(
				(i) =>
					i.FileName.toLowerCase().includes(search.toLowerCase()) ||
					(i.Tags || "").toLowerCase().includes(search.toLowerCase()) ||
					(i.Description || "").toLowerCase().includes(search.toLowerCase()) ||
					(i.UploadedBy || "").toLowerCase().includes(search.toLowerCase())
			);
	}, [items, search]);

	const totalPages = Math.ceil(filteredItems.length / pageSize);
	const currentItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

	return (
		<div className="w-full  flex-1 overflow-y-auto p-4">
			{/* Search + Add */}
			<div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
				<input
					type="text"
					placeholder="Search by filename, description, uploader name, tags..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
				/>
				<button
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowModal(true)}
				>
					Upload Document
				</button>
			</div>

			{/* Document Table */}
			<div className="overflow-x-auto rounded-md border border-gray-200">
				<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-3 py-4 text-left font-medium text-gray-700 w-12">#</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">File Name</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">Description</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">Type</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">Size</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">Uploaded By</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">On</th>
							<th className="px-3 py-4 text-left font-medium text-gray-700">Tags</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{currentItems.length === 0 && (
							<tr>
								<td colSpan={9} className="px-3 py-4 text-center text-gray-500 italic">
									{loading ? (
										<p className="text-center text-blue-400 italic">Loading...</p>
									) : (
										<p className="text-center text-gray-500 italic">No documents found</p>
									)}
								</td>
							</tr>
						)}
						{currentItems.map((doc, idx) => (
							<tr key={doc.DocumentID} className="hover:bg-gray-300">
								<td className="px-3 py-2 text-gray-600">{idx + 1 + (page - 1) * pageSize}</td>
								<td className="px-3 py-2 font-medium text-blue-600 break-words">{doc.FileName}</td>
								<td className="px-3 py-2 text-gray-600 break-words">{doc.Description}</td>
								<td className="px-3 py-2 text-gray-600">{doc.FileType}</td>
								<td className="px-3 py-2 text-gray-600">{(doc.FileSize / 1024).toFixed(1)} KB</td>
								<td className="px-3 py-2 text-gray-600">{doc.UploadedBy}</td>
								<td className="px-3 py-2 text-gray-600">
									{new Date(doc.UploadedOn).toLocaleString(undefined, {
										year: "numeric",
										month: "short",
										day: "numeric",
										hour: "2-digit",
										minute: "2-digit",
									})}
								</td>
								<td className="px-3 py-2 text-gray-600">{doc.Tags}</td>
								<td className="flex flex-row py-3">
									{/* Download Button */}
									<button
										className="px-2 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 cursor-pointer mr-2"
										onClick={async () => {
											try {
												const res = await fetch(`${apiBaseUrl}/api/documents/download/${doc.DocumentID}`);
												if (!res.ok) throw new Error("Failed to get download URL");

												const blob = await res.blob();
												const actualFileName = res.headers.get("x-blob-name") || doc.FileName;

												const link = document.createElement("a");
												link.href = window.URL.createObjectURL(blob);
												link.download = actualFileName;
												document.body.appendChild(link);
												link.click();
												link.remove();
											} catch (err) {
												console.error("Download error:", err);
												alert("Failed to download file");
											}
										}}
									>
										Download
									</button>

									{/* Delete Button */}
									<button
										className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
										onClick={() => handleDelete(doc.DocumentID)}
									>
										Delete
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-3">
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage((p) => Math.max(p - 1, 1))}
						disabled={page === 1}
					>
						Prev
					</button>
					<span className="text-sm text-gray-700">
						Page {page} of {totalPages}
					</span>
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
						disabled={page === totalPages}
					>
						Next
					</button>
				</div>
			)}

			{/* Upload Modal */}
			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4">Upload Document</h2>
						<div className="flex flex-col gap-3">
							<input
								type="file"
								className={`px-3 py-2 border rounded-md ${errors.file ? "border-red-500" : "border-gray-300"}`}
								onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files[0] })}
							/>
							{errors.file && (
								<p className="text-red-500 text-xs mt-1">
									{typeof errors.file === "string" ? errors.file : "File is required"}
								</p>
							)}

							<select
								className="px-3 py-2 border rounded-md border-gray-300"
								value={newDoc.fileType}
								onChange={(e) => setNewDoc({ ...newDoc, fileType: e.target.value })}
							>
								<option value="General">General</option>
								<option value="Finance">Finance</option>
								<option value="Ticket">Ticket</option>
								<option value="Other">Other</option>
							</select>

							<input
								type="text"
								placeholder="Description (max 50 chars)"
								maxLength={50}
								className={`px-3 py-2 border rounded-md ${errors.description ? "border-red-500" : "border-gray-300"}`}
								value={newDoc.description}
								onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
							/>
							{errors.description && <p className="text-red-500 text-xs mt-1">Description is required</p>}

							<input
								type="text"
								placeholder="Tags: Finance, Ticket, General etc. (required)"
								className={`px-3 py-2 border rounded-md ${errors.tags ? "border-red-500" : "border-gray-300"}`}
								value={newDoc.tags}
								onChange={(e) => setNewDoc({ ...newDoc, tags: e.target.value })}
							/>
							{errors.tags && <p className="text-red-500 text-xs mt-1">At least one tag is required</p>}
						</div>

						<div className="flex justify-end gap-2 mt-4">
							<button
								className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
								onClick={() => {
									setShowModal(false);
									setErrors({});
								}}
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
								onClick={handleAdd}
							>
								Upload
							</button>
						</div>

						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
							onClick={() => {
								setShowModal(false);
								setErrors({});
							}}
						>
							✕
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserDocumentStorage;
